import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core'
import {
  ActiveCartFacade,
  ConsignmentEntry,
  MultiCartFacade,
  SelectiveCartFacade
} from '@spartacus/cart/base/root'
import { UserIdService } from '@spartacus/core'
import { EbCartItemListComponent } from '../../../cart/components/cart-item-list/eb-cart-item-list.component'
import { EbOrderEntry } from '../../../cart/interface/eb-order-entry.interface'
import { EbAvailabilityService } from '../../../product/services/facades/eb-availability.service'
import { EbPricesService } from '../../../product/services/facades/eb-prices.service'

@Component({
    selector: 'eb-favorites-item-list',
    templateUrl: './eb-favorites-item-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EbFavoritesItemListComponent extends EbCartItemListComponent implements OnInit {
  @Input() isAllSelected = false
  @Output() singleItemSelected = new EventEmitter()
  @Output() allItemsSelected = new EventEmitter()
  @Output() removeSelectedItem = new EventEmitter()
  @Output() editCommentOnEntry = new EventEmitter()
  pageSize = 10
  currentPage = 1
  sortedFavoriteListItems: EbOrderEntry[]
  currentOrderEntries: EbOrderEntry[] = []
  plainFavoriteListItems: EbOrderEntry[]

  @Input('sortBy')
  set sortBy(sortOption) {
    this.sortedFavoriteListItems.sort(
      sortOption === 'creationTime'
        ? (a, b) => (a.entryNumber > b.entryNumber ? 1 : -1)
        : (a, b) => a.product.name.localeCompare(b.product.name)
    )
    this.resetList()
    this.setCurrentOrderEntries()
  }

  @Input('filterBy')
  set filterBy(term) {
    const searchTerm = term.toLowerCase()
    this.sortedFavoriteListItems = this.plainFavoriteListItems.filter(
      entry =>
        entry?.product?.code?.toLowerCase().includes(searchTerm) ||
        entry?.product?.name?.toLowerCase().includes(searchTerm) ||
        entry?.comment?.toLowerCase().includes(searchTerm)
    );
    if(this.sortedFavoriteListItems?.length > 0  && searchTerm){
      this.loadPricesForProducts( this.sortedFavoriteListItems)
      this.loadAvailabilityForProducts(this.sortedFavoriteListItems)
    }
    this.resetList()
    this.setCurrentOrderEntries()
  }

  @Input('items')
  set items(items: EbOrderEntry[]) {
    this.resetList()
    this.plainFavoriteListItems = items
    this.resolveItems(items)
    this.nextPage()
    if (this.loadPrices) {
      this.loadPricesForProducts(this.currentOrderEntries)
    }
    if (this.loadAvailabilities) {
      this.loadAvailabilityForProducts(this.currentOrderEntries)
    }
    this.cd.detectChanges()
  }

  get items(): EbOrderEntry[] {
    return this.sortedFavoriteListItems
  }

  constructor(
    activeCartService: ActiveCartFacade,
    selectiveCartService: SelectiveCartFacade,
    userIdService: UserIdService,
    multiCartService: MultiCartFacade,
    protected pricesService: EbPricesService,
    protected availabilityService: EbAvailabilityService,
    protected readonly cd: ChangeDetectorRef
  ) {
    super(
      activeCartService,
      selectiveCartService,
      userIdService,
      multiCartService,
      cd,
      availabilityService,
      pricesService
    )
  }

  protected resolveItems(items: EbOrderEntry[]) {
    if (items.every(item => item.hasOwnProperty('orderEntry'))) {
      this.sortedFavoriteListItems = items.map(consignmentEntry => {
        const entry: EbOrderEntry = Object.assign(
          {},
          (consignmentEntry as ConsignmentEntry).orderEntry
        )
        entry.quantity = consignmentEntry.quantity
        return entry
      })
    } else {
      this.sortedFavoriteListItems = items
    }
  }

  nextPage() {
    if (this.currentOrderEntries.length >= this.pageSize) {
      this.currentPage = this.currentPage + 1
    }
    this.setCurrentOrderEntries()
    this.prefetchNextPage()
  }

  private prefetchNextPage() {
    const indexOfNextPageLastEntry = (this.currentPage + 1) * this.pageSize
    const indexOfNextPageFirstEntry = indexOfNextPageLastEntry - this.pageSize
    this.loadPricesForProducts(
      this.sortedFavoriteListItems.slice(indexOfNextPageFirstEntry, indexOfNextPageLastEntry)
    )
    this.loadAvailabilityForProducts(
      this.sortedFavoriteListItems.slice(indexOfNextPageFirstEntry, indexOfNextPageLastEntry)
    )
  }

  onSelectedItemChange(item: EbOrderEntry) {
    if (item.isSelected) {
      item.totalPrice = this.productPrices.get(
        item.product?.successor ? item.product.successor.code : item.product.code
      )?.items?.[0]?.itemNetPricePerUnit
    }
    this.singleItemSelected.emit(item)
  }

  private setCurrentOrderEntries(): void {
    const indexOfLastEntry = this.currentPage * this.pageSize
    const indexOfFirstEntry = indexOfLastEntry - this.pageSize
    this.currentOrderEntries = [
      ...this.currentOrderEntries,
      ...this.sortedFavoriteListItems.slice(indexOfFirstEntry, indexOfLastEntry)
    ]
    this.cd.detectChanges()
  }

  onRemoveSelectedItem(productCode: string) {
    this.removeSelectedItem.emit(productCode)
  }

  onEditCommentOnEntry(item: { entryNumber: number; comment: string }) {
    this.editCommentOnEntry.emit(item)
  }

  selectAllItems(isChecked: boolean) {
    this.items.forEach(item => (item.isSelected = isChecked))
    if (isChecked) {
      this.items.forEach(item => {
        item.totalPrice = this.productPrices.get(
          item.product?.successor ? item.product.successor.code : item.product.code
        )?.items?.[0]?.itemNetPricePerUnit
      })
    }
    this.allItemsSelected.emit(this.items)
  }

  resetList() {
    this.currentPage = 1
    this.currentOrderEntries = []
  }
}
